import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Flex,
  Stack,
  Radio,
  RadioGroup,
  Input,
  Box,
  Tooltip,
  Grid,
  GridItem,
  IconButton,
  Icon,
} from "@chakra-ui/react";
import {
  textPrimary,
  textSecondary,
  newBorder,
  buttonRed,
  buttonRedText,
  secondary,
  white,
  primaryAlt,
  backgroundLight,
} from "../../utils/colors";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import useIsMobile from "../../hooks/useIsMobile";
import { FaXmark } from "react-icons/fa6";

const ConfirmCancelPublicModal = ({
  isOpen,
  onClose,
  confirmAction,
  treatmentPlanDetails,
  reason,
  reasonComment,
  setReason,
  setReasonComment,
}) => {
  const { t } = useTranslation();

  const isMobile = useIsMobile();

  // console.log("treatmentPlanDetails", treatmentPlanDetails);

  const reasons = [
    { value: "price", label: t("reason.price"), text: t("reason.priceText") },
    { value: "hr", label: t("reason.hr"), text: t("reason.hrText") },
    {
      value: "treatment",
      label: t("reason.treatment"),
      text: t("reason.treatmentText"),
    },
    {
      value: "custom",
      label: t("reason.custom"),
      text: t("reason.customText"),
    },
  ];

  const handleConfirmAction = () => {
    // Implement the logic to handle confirmation with the selected reason
    // For custom reason, use the customReason state value

    confirmAction();
  };

  const CustomRadio = ({ value, label, text }) => (
    <Box
      onClick={() => setReason(value)}
      p="1rem 1rem"
      borderWidth="1px"
      borderRadius="0.875rem"
      borderColor={reason === value ? primaryAlt : newBorder}
      color={reason === value ? primaryAlt : textSecondary}
      bg={reason === value ? "" : ""}
      cursor="pointer"
      w={["100%", "25%"]}
      transform="all 0.2s"
      _hover={{
        borderColor: reason === value ? primaryAlt : "#ddd",
        color: reason === value ? primaryAlt : textPrimary,
        // bg: reason === value ? "red.50" : "",
      }}
    >
      <Text fontWeight="500" fontSize="sm">
        {label}
      </Text>
      {/* <Text fontSize="13px" color={textSecondary}>
        {text}
      </Text> */}
    </Box>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered={isMobile ? false : true}
    >
      <ModalOverlay />
      <ModalContent
        borderRadius={{ base: "0", md: "1.5rem" }}
        w={{ base: "100%", md: "600px" }}
        minH={{ base: "100%", md: "auto" }}
        maxW="100vw"
        m={0}
        border={`1px solid ${newBorder}`}
      >
        <ModalHeader
          w="100%"
          color={textPrimary}
          maxW={["100%", "100%", "100%"]}
          m="0 auto"
          fontSize={["1rem", "1rem", "1.125rem"]}
          p={["1rem 1.5rem", "1rem 1.5rem 1.5rem"]}
          borderBottom={`1px solid ${newBorder}`}
          fontWeight="500"
        >
          <Flex w="100%" align="center" justify="space-between">
            <Text>
              {t("common.cancelTreatmentPlanConfirmationTitlePublic")}
            </Text>

            <IconButton
              size="sm"
              bg={backgroundLight}
              color={textSecondary}
              onClick={onClose}
              borderRadius="10rem"
              _hover={{ bg: primaryAlt, color: white }}
            >
              <Icon as={FaXmark} />
            </IconButton>
          </Flex>
        </ModalHeader>

        <ModalBody
          p="0rem 0rem 0"
          color={textSecondary}
          fontWeight="500"
          fontSize="1rem"
        >
          <Box
            w="100%"
            p="1.5rem 1.5rem 2rem"
            borderBottom={`1px solid ${newBorder}`}
          >
            <Flex
              w="100%"
              align="center"
              justify="center"
              m="1rem 0 1rem"
              direction="column"
            >
              <Text
                fontSize="1.25rem"
                color={textPrimary}
                fontWeight="500"
                textAlign="center"
              >
                {t("treatmentPlan.treatmentPlan")} -{" "}
                <Text as="span">{treatmentPlanDetails?.treatmentPlanID}</Text>
              </Text>

              <Flex
                align="center"
                justify="center"
                direction="column"
                m="0.5rem 0 0"
              >
                <Flex align="center">
                  <Text
                    fontSize="sm"
                    color={textSecondary}
                    fontWeight="500"
                    textAlign="center"
                    mr={1}
                  >
                    {t("treatmentPlan.createdAt")}:
                  </Text>
                  <Text
                    fontSize="sm"
                    color={textSecondary}
                    fontWeight="500"
                    textAlign="center"
                  >
                    {treatmentPlanDetails &&
                      dayjs(treatmentPlanDetails.createdAt).format(
                        "YYYY-MM-DD"
                      )}
                  </Text>
                </Flex>
                <Flex align="center">
                  <Text
                    fontSize="sm"
                    color={textSecondary}
                    fontWeight="500"
                    textAlign="center"
                    mr={1}
                  >
                    {t("treatmentPlan.treatmentPlanPrice")}:
                  </Text>
                  <Text
                    fontSize="sm"
                    color={textSecondary}
                    fontWeight="500"
                    textAlign="center"
                  >
                    {treatmentPlanDetails && treatmentPlanDetails.totalPrice} €
                  </Text>
                </Flex>
              </Flex>
            </Flex>

            <Flex
              p={["0.5rem 0rem 2rem", "1rem 2rem 1rem"]}
              // borderTop={`1px solid ${newBorder}`}
            >
              <Stack w="100%" p="0rem 0">
                <Text fontSize="1rem" color={textPrimary} fontWeight="500">
                  {t("treatmentPlan.cancelReasonTitle")}
                </Text>

                <Box w="100%">
                  <Flex
                    w="100%"
                    gap="0.5rem"
                    m="0.5rem 0 0.25rem"
                    direction={["column", "row"]}
                  >
                    {reasons.map((reason) => (
                      <CustomRadio
                        key={reason.value}
                        value={reason.value}
                        label={reason.label}
                        text={reason.text}
                      />
                    ))}
                  </Flex>

                  <Flex w="100%">
                    {reason === "custom" && (
                      <Input
                        placeholder={t("reason.customPlaceholder")}
                        value={reasonComment}
                        onChange={(e) => setReasonComment(e.target.value)}
                        size="lg"
                        fontSize="sm"
                        // borderRadius="1rem"
                        mt={4}
                        fontWeight="500"
                        w={["100%", "initial"]}
                        color={textPrimary}
                        border={`1px solid ${newBorder}`}
                        borderRadius="0.75rem"
                        boxShadow="none !important"
                      />
                    )}
                  </Flex>
                </Box>
              </Stack>
            </Flex>
          </Box>
          <Flex
            justifyContent="center"
            alignItems="center"
            p={["1.5rem 1.5rem ", "1.5rem 0 "]}
            gap="1rem"
          >
            <Button
              // variant="ghost"
              bg={backgroundLight}
              color={textSecondary}
              size="lg"
              fontSize="sm"
              borderRadius="10rem"
              onClick={onClose}
              w={["50%", "auto"]}
            >
              {t("button.close")}
            </Button>
            <Tooltip
              label={t("reason.tooltipSelectReason")}
              fontSize="xs"
              placement="top"
              borderRadius="1rem"
              p="0.5rem 1rem"
              textAlign="center"
              isDisabled={reason}
            >
              <Button
                colorScheme="red"
                size="lg"
                fontSize="sm"
                borderRadius="10rem"
                onClick={handleConfirmAction}
                isDisabled={!reason}
                w={["50%", "auto"]}
              >
                {t("treatmentPlan.cancelPlan")}
              </Button>
            </Tooltip>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ConfirmCancelPublicModal;
